import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, Alert, Typography, Space, Divider, Flex } from "antd"
import { MailOutlined, LockOutlined, PhoneOutlined, NumberOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import Spinner from "components/common/Spinner"
import {
  verifyGuestEmail,
  getGuestVerificationMethod,
  sendGuestBookingOtp,
  verifyGuestBookingOtp
} from "store/guest-access"
import { routes } from "router"

const { Title, Text } = Typography

/**
 * Shown to logged-out users who arrive at a booking details page via an email/SMS link.
 * Confirms identity before granting access to the booking:
 * - Clients with a real email on file verify by entering that email.
 * - Phone-only clients (no real email, e.g. imported without one) verify via SMS OTP.
 */
const GuestEmailVerifyModal = ({ bookingUuid }) => {
  const [emailForm] = Form.useForm()
  const [phoneForm] = Form.useForm()
  const [otpForm] = Form.useForm()
  const [phoneNumber, setPhoneNumber] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error, method, otpSent } = useSelector((state) => state.guestAccess)

  useEffect(() => {
    dispatch(getGuestVerificationMethod(bookingUuid))
  }, [bookingUuid]) //eslint-disable-line

  const handleEmailSubmit = async (values) => {
    try {
      await dispatch(verifyGuestEmail(bookingUuid, values.email))
      // Success: Redux state updates authorized=true, component unmounts via parent re-render
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const handlePhoneSubmit = async (values) => {
    try {
      await dispatch(sendGuestBookingOtp(bookingUuid, values.phoneNumber))
      setPhoneNumber(values.phoneNumber)
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const handleOtpSubmit = async (values) => {
    try {
      await dispatch(verifyGuestBookingOtp(bookingUuid, values.otp))
      // Success: Redux state updates authorized=true, component unmounts via parent re-render
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const renderEmailStep = () => (
    <Form form={emailForm} onFinish={handleEmailSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="email"
        label="Email Address"
        rules={[
          { required: true, message: "Please enter your email address" },
          { type: "email", message: "Please enter a valid email address" }
        ]}
      >
        <Input
          prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="your@email.com"
          size="large"
          autoComplete="email"
          autoFocus
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          Access Trip Details
        </Button>
      </Form.Item>
    </Form>
  )

  const renderPhoneStep = () => (
    <Form form={phoneForm} onFinish={handlePhoneSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="phoneNumber"
        label="Phone Number"
        rules={[{ required: true, message: "Please enter your phone number" }]}
      >
        <Input
          prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="(555) 555-5555"
          size="large"
          autoComplete="tel"
          autoFocus
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          Send Verification Code
        </Button>
      </Form.Item>
    </Form>
  )

  const renderOtpStep = () => (
    <Form form={otpForm} onFinish={handleOtpSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="otp"
        label="Verification Code"
        rules={[{ required: true, message: "Please enter the code we texted you" }]}
      >
        <Input
          prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="12345"
          size="large"
          autoComplete="one-time-code"
          autoFocus
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          Access Trip Details
        </Button>
      </Form.Item>
    </Form>
  )

  const isDeterminingMethod = method === null
  const isPhoneFlow = method === "phone"
  const description = isPhoneFlow
    ? otpSent
      ? `Enter the verification code we texted to ${phoneNumber}.`
      : "Enter the phone number associated with this booking to receive a verification code."
    : "Enter the email address associated with this booking to view your trip details."

  return (
    <Flex align="center" justify="center" style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Modal
        open={true}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={440}
        styles={{ mask: { background: "transparent" } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Flex align="center" justify="center" style={{ width: 56, height: 56, borderRadius: "50%", background: "#e6f4ff", margin: "0 auto 16px" }}>
              <LockOutlined style={{ fontSize: 24, color: "#1677ff" }} />
            </Flex>
            <Title level={4} style={{ marginBottom: 4 }}>
              Verify Your Identity
            </Title>
            {!isDeterminingMethod && <Text type="secondary">{description}</Text>}
          </div>

          {isDeterminingMethod ? (
            <Spinner />
          ) : (
            <>
              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  icon={isPhoneFlow ? <PhoneOutlined /> : <MailOutlined />}
                />
              )}

              {isPhoneFlow ? (otpSent ? renderOtpStep() : renderPhoneStep()) : renderEmailStep()}

              <Divider plain style={{ margin: "0" }}>
                <Text type="secondary" style={{ fontSize: 13 }}>or</Text>
              </Divider>

              <Button size="large" block onClick={() => navigate(routes.signInV2())}>
                Sign In
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </Flex>
  )
}

GuestEmailVerifyModal.propTypes = {
  bookingUuid: PropTypes.string.isRequired
}

export default GuestEmailVerifyModal
